import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Users, Check, AlertCircle, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WEBHOOK_SECRET = 'xnVQ4eWsLVXlg0IyAvJD6mR5Ker8fm2e3wcR3XUPVgw';

function BulkImport() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  function handleFileChange(e) {
    setFile(e.target.files[0]);
    setResult(null);
    setError(null);
  }

  async function handleUpload() {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResult(null);

    var formData = new FormData();
    formData.append('file', file);
    formData.append('secret', WEBHOOK_SECRET);

    try {
      var res = await fetch(BACKEND_URL + '/api/memberspace/bulk-import-csv', {
        method: 'POST',
        body: formData
      });
      var data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      setResult(data);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  function downloadCredentials() {
    if (!result) return;
    var created = result.details.created;
    if (!created || created.length === 0) return;
    var lines = ['email,temp_password'];
    for (var i = 0; i < created.length; i++) {
      lines.push(created[i].email + ',' + created[i].temp_password);
    }
    var blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = 'member_credentials.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function renderResults() {
    if (!result) return null;

    var hasCreated = result.details && result.details.created && result.details.created.length > 0;
    var hasFailed = result.details && result.details.failed && result.details.failed.length > 0;
    var failedItems = hasFailed ? result.details.failed : [];

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card border-2 border-border rounded-2xl p-6 brutalist-shadow space-y-4"
        data-testid="import-results"
      >
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Check className="w-5 h-5 text-emerald-500" />
          Import Complete
        </h3>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-emerald-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold font-mono text-emerald-600">{result.created}</div>
            <div className="text-xs text-emerald-600">New accounts</div>
          </div>
          <div className="bg-blue-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold font-mono text-blue-600">{result.existing_updated}</div>
            <div className="text-xs text-blue-600">Updated</div>
          </div>
          <div className="bg-rose-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold font-mono text-rose-600">{result.failed}</div>
            <div className="text-xs text-rose-600">Failed</div>
          </div>
        </div>

        {hasCreated && (
          <div>
            <p className="text-sm text-muted-foreground mb-3">
              Download the credentials file to share login details with your new members.
            </p>
            <Button onClick={downloadCredentials} variant="outline" className="w-full" data-testid="download-credentials-btn">
              <Download className="w-4 h-4 mr-2" />
              Download Credentials CSV
            </Button>
          </div>
        )}

        {hasFailed && (
          <div className="bg-rose-50 rounded-xl p-3">
            <p className="text-sm font-semibold text-rose-700 mb-2">Failed imports:</p>
            {failedItems.map(function(f, i) {
              return <p key={i} className="text-xs text-rose-600 font-mono">{f.email}: {f.reason}</p>;
            })}
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen noise-bg" data-testid="bulk-import-page">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={function() { navigate('/'); }} className="btn-press" data-testid="bulk-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold">Bulk Member Import</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 py-12 max-w-2xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
          <div className="bg-card border-2 border-border rounded-2xl p-6 brutalist-shadow" data-testid="import-instructions">
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Import Gospel Pattern Secrets Members
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Upload a CSV or text file with your members' emails. One email per line — that's it!
            </p>
            <div className="bg-secondary rounded-xl p-4 text-sm font-mono">
              <p className="text-muted-foreground mb-1">Just list the emails:</p>
              <p>john@example.com</p>
              <p>jane@example.com</p>
              <p>mike@example.com</p>
            </div>
          </div>

          <div className="bg-card border-2 border-border rounded-2xl p-6 brutalist-shadow">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Select CSV File</label>
                <Input type="file" accept=".csv,.txt" onChange={handleFileChange} className="cursor-pointer" data-testid="csv-file-input" />
              </div>
              {file && (
                <p className="text-sm text-muted-foreground">
                  Selected: <span className="font-mono font-semibold">{file.name}</span>
                </p>
              )}
              <Button onClick={handleUpload} disabled={!file || loading} className="w-full bg-accent hover:bg-accent/90 text-white" data-testid="import-submit-btn">
                <Upload className="w-4 h-4 mr-2" />
                {loading ? 'Importing...' : 'Import Members'}
              </Button>
            </div>
          </div>

          {error && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-start gap-3" data-testid="import-error">
              <AlertCircle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-rose-700">{error}</span>
            </motion.div>
          )}

          {renderResults()}
        </motion.div>
      </main>
    </div>
  );
}

export default BulkImport;
